import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import type { VideoSource } from '../../../../shared/protocol';
import type { PlayerState, VideoPlayerRef } from '../../types/video';
import { getVideoCapabilities } from '../../lib/video/playerCapabilities';

type TwitchSource = Extract<VideoSource, { provider: 'twitch' }>;

interface TwitchPlayerApi {
  play(): void;
  pause(): void;
  seek(seconds: number): void;
  getCurrentTime(): number;
  getDuration(): number;
  isPaused(): boolean;
  addEventListener(event: string, callback: () => void): void;
}

interface TwitchPlayerConstructor {
  new(target: string, options: Record<string, string | number | boolean | string[]>): TwitchPlayerApi;
  READY: string;
  PLAY: string;
  PAUSE: string;
  SEEK: string;
}

declare global {
  interface Window { Twitch?: { Player?: TwitchPlayerConstructor } }
}

let twitchApiPromise: Promise<TwitchPlayerConstructor> | null = null;

function loadTwitchApi(): Promise<TwitchPlayerConstructor> {
  if (window.Twitch?.Player) return Promise.resolve(window.Twitch.Player);
  if (twitchApiPromise) return twitchApiPromise;
  twitchApiPromise = new Promise<TwitchPlayerConstructor>((resolve, reject) => {
    let script = document.querySelector<HTMLScriptElement>('script[data-twitch-player-api]');
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://player.twitch.tv/js/embed/v1.js';
      script.async = true;
      script.dataset.twitchPlayerApi = 'true';
      document.head.appendChild(script);
    }
    script.addEventListener('load', () => {
      if (window.Twitch?.Player) resolve(window.Twitch.Player);
      else reject(new Error('Twitch Player API indisponível.'));
    }, { once: true });
    script.addEventListener('error', () => reject(new Error('Não foi possível carregar o player oficial do Twitch.')), { once: true });
  }).catch((error: unknown) => {
    twitchApiPromise = null;
    throw error;
  });
  return twitchApiPromise as Promise<TwitchPlayerConstructor>;
}

export const TwitchVideoPlayer = forwardRef<VideoPlayerRef, {
  source: TwitchSource;
  onReady?: () => void;
  onStateChange?: (state: PlayerState, position: number) => void;
  onSeek?: (position: number) => void;
  onError?: (message: string) => void;
}>(function TwitchVideoPlayer({ source, onReady, onStateChange, onSeek, onError }, ref) {
  const generatedId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const targetId = `twitch-player-${generatedId}`;
  const playerRef = useRef<TwitchPlayerApi | null>(null);
  const suppressSeekRef = useRef(false);
  const [ready, setReady] = useState(false);
  const capabilities = getVideoCapabilities(source);
  const sourceKind = source.kind;
  const sourceId = source.id;
  const currentPosition = () => capabilities.canGetPosition ? playerRef.current?.getCurrentTime() ?? 0 : 0;

  useEffect(() => {
    if (sourceKind === 'clip') return undefined;
    let cancelled = false;
    void loadTwitchApi().then((Player) => {
      if (cancelled) return;
      const playerOptions: Record<string, string | number | boolean | string[]> = {
        width: '100%', height: '100%', parent: [window.location.hostname], autoplay: false, layout: 'video',
        ...(sourceKind === 'channel' ? { channel: sourceId } : { video: sourceId }),
      };
      const player = new Player(targetId, playerOptions);
      playerRef.current = player;
      player.addEventListener(Player.READY, () => {
        if (cancelled) return;
        setReady(true);
        onReady?.();
      });
      player.addEventListener(Player.PLAY, () => { if (!cancelled) onStateChange?.('playing', currentPosition()); });
      player.addEventListener(Player.PAUSE, () => { if (!cancelled) onStateChange?.('paused', currentPosition()); });
      player.addEventListener(Player.SEEK, () => {
        if (!cancelled && !suppressSeekRef.current && capabilities.canGetPosition) onSeek?.(currentPosition());
      });
    }).catch((error: unknown) => {
      if (!cancelled) onError?.(error instanceof Error ? error.message : 'Não foi possível carregar o Twitch.');
    });
    return () => {
      cancelled = true;
      playerRef.current = null;
      document.getElementById(targetId)?.replaceChildren();
    };
  }, [targetId, sourceKind, sourceId, onReady, onStateChange, onSeek, onError]);

  useImperativeHandle(ref, () => ({
    capabilities,
    play: async () => { playerRef.current?.play(); },
    pause: async () => { playerRef.current?.pause(); },
    seekTo: async (seconds) => {
      if (!capabilities.canSeek) return;
      suppressSeekRef.current = true;
      playerRef.current?.seek(Math.max(0, seconds));
      window.setTimeout(() => { suppressSeekRef.current = false; }, 1_000);
    },
    getCurrentTime: currentPosition,
    getDuration: () => capabilities.canGetPosition ? playerRef.current?.getDuration() ?? 0 : 0,
    getState: () => !ready ? 'unstarted' : playerRef.current?.isPaused() ? 'paused' : 'playing',
    isReady: () => ready,
  }), [capabilities, ready]);

  if (source.kind === 'clip') {
    const embed = new URL('https://clips.twitch.tv/embed');
    embed.searchParams.set('clip', source.id);
    embed.searchParams.set('parent', window.location.hostname);
    embed.searchParams.set('preload', 'metadata');
    return <>
      <div className="video-frame" aria-label="Clip do Twitch">
        <iframe className="video-embed external-iframe" src={embed.toString()} title="Clip do Twitch" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />
      </div>
      <div className="provider-note"><span>Clips do Twitch não oferecem a Player API; este conteúdo não pode ser sincronizado.</span><a href={`https://clips.twitch.tv/${encodeURIComponent(source.id)}`} target="_blank" rel="noreferrer">Abrir origem</a></div>
    </>;
  }

  const origin = source.kind === 'channel'
    ? `https://www.twitch.tv/${encodeURIComponent(source.id)}`
    : `https://www.twitch.tv/videos/${encodeURIComponent(source.id)}`;
  return <>
    <div className="video-frame" aria-label={source.kind === 'channel' ? 'Live do Twitch' : 'VOD do Twitch'}>
      <div id={targetId} className="video-embed twitch-embed" />
      {!ready && <div className="player-loading">Carregando Twitch...</div>}
    </div>
    <div className="provider-note"><span>{source.kind === 'channel' ? 'Live: play/pause sincronizados; posição e seek não são fornecidos pelo Twitch.' : 'Player Twitch VOD sincronizado.'}</span><a href={origin} target="_blank" rel="noreferrer">Abrir origem</a></div>
  </>;
});
